import React, { useContext } from 'react';
import classNames from 'classnames';
import { useProductBandQuery } from '@/api/product/bands';
import { BuyButtonComponentProps } from '@/components/features/cms/blocks/BuyButton/register';
import { Button, Drawer, Icon, InputCheckbox, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import styles from './BuyButton.module.scss';
import { ProductLine } from './ProductLine';

/**
 * TODO:
 *  - Add support for adding a product to the basket
 *  - Add support for showing variants, if any - it should only be possible to add variants to the basket,
 *    if they exist under a master product
 */

/**
 * CMS BuyButton component, used for opening Drawer with products passed to it.
 */
export const BuyButton: React.FunctionComponent<BuyButtonComponentProps> = ({ products, buttonVariant }) => {
	const productArray = products.map((p) => p.id) ?? [];

	const drawerRef = React.useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: results } = useProductBandQuery(productArray, isOpen, settings?.data.searchSettings);

	const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);

	const handleProductChange = React.useCallback((e): void => {
		const { checked, id } = e.target;
		setSelectedProducts((prev) => {
			if (checked) return [...prev, id];

			return prev.filter((product) => product !== id);
		});
	}, []);

	return (
		<>
			{/* Trigger button */}
			{buttonVariant === 'round' ? (
				<Button
					className={styles.roundButton}
					hasOnlyIcon
					variant={'secondary'}
					size={'sm'}
					aria-label={translations?.data.shared.showMore ?? 'Show more'}
					onClick={handleOpen}
				>
					<Icon name={'plus'} size={'md'} />
				</Button>
			) : (
				<Button hasOnlyIcon aria-label={translations?.data.shared.showMore ?? 'Show more'} onClick={handleOpen}>
					<Icon name={'basket'} size={'md'} />
				</Button>
			)}

			{/* Menu */}
			{isRendered && (
				<Drawer drawerRef={drawerRef} direction={'Right'} desktopWidth={'400px'} isOpen={isOpen}>
					<Drawer.Level isActive={true}>
						<Drawer.Header title={'Inspiration'} handleClose={handleClose} />
						<Drawer.Body>
							<fieldset className={classNames(styles.products, 'u-container')}>
								{results?.map((product) => (
									<InputCheckbox
										className={styles.product}
										label={<ProductLine {...product} />}
										id={product.id}
										checked={false}
										name={'pizzaTopping'}
										onChange={handleProductChange}
										key={product.id}
									/>
								))}
							</fieldset>
						</Drawer.Body>
						<Drawer.Footer>
							<Button disabled={!selectedProducts.length} isFullWidth>
								Add products to basket
							</Button>
						</Drawer.Footer>
					</Drawer.Level>
				</Drawer>
			)}
		</>
	);
};
